import { inject, NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { RouteType } from "../models/enum";
import { RoleService } from "../shared/services/role.service";
import { ActionCenterGuard } from "../services/action-center-guard.service";
import { AdminService as AdminGuard } from "../services/admin-guard.service";
import { AuthGuardService as AuthGuard } from "../services/auth-guard.service";
import { GroupAdminOrAdminGuard } from "../services/group-admin-or-admin-guard.service";
import { InstructorService as InstructorGuard } from "../services/instructor-guard.service";
import { OfficeAdminGuard } from "../services/office-admin-guard.service";
import { unsavedChangesGuard } from "../services/unsaved-changes.guard";
import { CbtaWebComponent } from "./cbta-web.component";
import { ChecklistDetailComponent } from "./checklist/checklist-detail.component";
import { ChecklistComponent } from "./checklist/checklist.component";
import { ChecklistFeedbackRouteComponent } from "./checklist/feedback/checklist-feedback-route.component";
import { FeedbackFormComponent } from "./feedback/feedback-form/feedback-form.component";
import { FeedbackManagementComponent } from "./feedback/feedback-management/feedback-management.component";
import { FeedbackResponseDetailComponent } from "./feedback/feedback-response-detail/feedback-response-detail.component";
import { FeedbackResponsesComponent } from "./feedback/feedback-responses/feedback-responses.component";
import { MyFeedbackDetailComponent } from "./feedback/my-feedback-detail/my-feedback-detail.component";
import { MyFeedbackComponent } from "./feedback/my-feedback/my-feedback.component";
import { CreateFormComponent } from "./forms/create-form/create-form.component";
import { CreateUserComponent } from "./forms/create-user/create-user.component";
import { EditFormComponent } from "./forms/edit-form/edit-form.component";
import { EditUserComponent } from "./forms/edit-user/edit-user.component";
import { FormsComponent } from "./forms/forms.component";
import { UserManagementComponent } from "./forms/user-management/user-management.component";
import { InstructorVerificationComponent } from "./instructor-verification/instructor-verification.component";
import { PendingComponent } from "./pending/pending.component";
import { PilotDataFilesComponent } from "./pilot-data/files/files.component";
import { PilotDataFoldersComponent } from "./pilot-data/folders/folders.component";
import { PilotDataComponent } from "./pilot-data/pilot-data.component";
import { PilotDataUploadComponent } from "./pilot-data/upload/upload.component";
import { ProfileComponent } from "./profile/profile.component";
import { ContainerRatingsFormComponent } from "./ratings/container-ratings-form/container-ratings-form.component";
import { QuickRatingSummaryComponent } from "./ratings/container-ratings-form/shared-rating-form/candidate-details/quick-rating-summary/quick-rating-summary.component";
import { QuickRatingSummaryDetailsComponent } from "./ratings/container-ratings-form/shared-rating-form/quick-rating-summary-details/quick-rating-summary-details.component";
import { RatingsComponent } from "./ratings/ratings.component";
import { ReportSummaryComponent } from "./report/report-summary/report-summary.component";
import { ReportComponent } from "./report/report.component";
import { TrainingDetailsComponent } from "./report/training-details/training-details.component";
import { TestOfflineRatingComponent } from "./services/test-offline-rating.component";

export const routes: Routes = [
	{
		path: "",
		component: CbtaWebComponent,
		children: [
			{
				path: "",
				redirectTo: () => inject(RoleService).getDefaultRoute(),
				pathMatch: "full",
			},
			{
				path: RouteType.DASHBOARD,
				component: ReportComponent,
				canActivate: [AuthGuard],
			},
			{
				path: RouteType.REPORT,
				component: ReportComponent,
				canActivate: [AuthGuard],
			},
			{
				path: RouteType.REPORT_SUMMARY,
				component: ReportSummaryComponent,
				canActivate: [AuthGuard],
			},
			{
				path: RouteType.REPORT_DETAILS,
				component: TrainingDetailsComponent,
				canActivate: [AuthGuard],
			},
			{
				path: RouteType.FORMS,
				component: FormsComponent,
				canActivate: [GroupAdminOrAdminGuard],
			},
			{
				path: RouteType.NEW_FORM,
				component: CreateFormComponent,
				canActivate: [AdminGuard],
				canDeactivate: [unsavedChangesGuard],
			},
			{
				path: RouteType.EDIT_FORM,
				component: EditFormComponent,
				canActivate: [GroupAdminOrAdminGuard],
				canDeactivate: [unsavedChangesGuard],
			},
			{
				path: RouteType.RATINGS,
				component: RatingsComponent,
				canActivate: [InstructorGuard],
			},
			// {
			// 	path: RouteType.TS1_RATING_FORM,
			// 	component: Ts1RatingFormComponent,
			// 	canActivate: [InstructorGuard],
			// },
			{
				path: RouteType.TS1_RATING_FORM,
				component: ContainerRatingsFormComponent,
				canActivate: [InstructorGuard],
			},
			{
				path: RouteType.TS1_RATING_FORM_DRAFT,
				component: ContainerRatingsFormComponent,
				canActivate: [InstructorGuard],
			},
			{
				path: RouteType.RATING_QUICK_SUMMARY,
				component: QuickRatingSummaryComponent,
				canActivate: [InstructorGuard],
			},
			{
				path: RouteType.RATING_QUICK_SUMMARY_DETAILS,
				component: QuickRatingSummaryDetailsComponent,
				canActivate: [InstructorGuard],
			},
			{
				path: RouteType.INSTRUCTOR_VERIFICATION,
				component: InstructorVerificationComponent,
				canActivate: [AdminGuard],
			},
			{
				path: RouteType.PROFILE,
				component: ProfileComponent,
				canActivate: [AuthGuard],
			},
			{
				path: RouteType.CHECKLIST,
				component: ChecklistComponent,
				canActivate: [AuthGuard, ActionCenterGuard],
			},
			{
				path: RouteType.CHECKLIST_STANDALONE_FEEDBACK,
				component: ChecklistFeedbackRouteComponent,
				canActivate: [AuthGuard, ActionCenterGuard],
			},
			{
				path: RouteType.CHECKLIST_DETAIL,
				component: ChecklistDetailComponent,
				canActivate: [AuthGuard, ActionCenterGuard],
			},
			{
				path: RouteType.CHECKLIST_FEEDBACK,
				component: ChecklistFeedbackRouteComponent,
				canActivate: [AuthGuard, ActionCenterGuard],
			},
			{
				path: RouteType.MY_FEEDBACK,
				component: MyFeedbackComponent,
				canActivate: [AuthGuard, ActionCenterGuard],
			},
			{
				path: RouteType.MY_FEEDBACK_DETAIL,
				component: MyFeedbackDetailComponent,
				canActivate: [AuthGuard, ActionCenterGuard],
			},
			{
				path: RouteType.PENDING,
				component: PendingComponent,
				canActivate: [InstructorGuard],
			},
			{
				path: RouteType.FEEDBACK,
				component: FeedbackManagementComponent,
				canActivate: [GroupAdminOrAdminGuard],
			},
			{
				path: RouteType.FEEDBACK_NEW,
				component: FeedbackFormComponent,
				canActivate: [AdminGuard],
				canDeactivate: [unsavedChangesGuard],
			},
			{
				path: RouteType.FEEDBACK_EDIT,
				component: FeedbackFormComponent,
				canActivate: [AdminGuard],
				canDeactivate: [unsavedChangesGuard],
			},
			{
				path: RouteType.FEEDBACK_RESPONSES,
				component: FeedbackResponsesComponent,
				canActivate: [GroupAdminOrAdminGuard],
			},
			{
				path: RouteType.FEEDBACK_RESPONSE,
				component: FeedbackResponseDetailComponent,
				canActivate: [GroupAdminOrAdminGuard],
			},
			{
				path: RouteType.USER_MANAGEMENT,
				component: UserManagementComponent,
				canActivate: [AdminGuard],
			},
			{
				path: RouteType.NEW_USER,
				component: CreateUserComponent,
				canActivate: [AdminGuard],
				canDeactivate: [unsavedChangesGuard],
			},
			{
				path: RouteType.EDIT_USER,
				component: EditUserComponent,
				canActivate: [AdminGuard],
				canDeactivate: [unsavedChangesGuard],
			},
			{
				path: RouteType.PILOT_DATA,
				component: PilotDataComponent,
				canActivate: [OfficeAdminGuard],
			},
			{
				path: RouteType.PILOT_DATA_UPLOAD,
				component: PilotDataUploadComponent,
				canActivate: [OfficeAdminGuard],
			},
			{
				path: RouteType.PILOT_DATA_FOLDERS,
				component: PilotDataFoldersComponent,
				canActivate: [OfficeAdminGuard],
			},
			{
				path: RouteType.PILOT_DATA_FILES,
				component: PilotDataFilesComponent,
				canActivate: [OfficeAdminGuard],
			},
			{
				path: "rating-calculator",
				component: TestOfflineRatingComponent,
			},
		],
	},
];

@NgModule({
	imports: [RouterModule.forChild(routes)],
	exports: [RouterModule],
})
export class CbtaWebRoutingModule {}
